import { useEffect, useState, useRef } from 'react';
import { Card, Heading, Container, VStack, Input, Textarea, Button, Box, Text, Icon, Image } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useParams, useNavigate } from 'react-router-dom';
import { toaster } from '@/components/ui/toaster';
import { Field } from '@/components/ui/field';
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import WorkspaceLayout from '@/pages/workspace/workspace-layout';
import ContentTypeActions from './actions/content-type-actions';
import useContentTypesStore from '@/utils/use-content-type';
import api from '@/utils/api';

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  sport_type_id: z.number().optional(),
  content_prompt: z.string().optional(),
  img_prompt: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

export default function EditContentLibrary() {
  const navigate = useNavigate();
  const { loadContentTypes } = useContentTypesStore();
  const [isBaseContentType, setIsBaseContentType] = useState<boolean>(true);
  const [uploadedSampleImageUrl, setUploadedSampleImageUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { workspaceId, id } = useParams<{
    workspaceId: string;
    id: string;
  }>();

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !workspaceId || !id) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('sample_file', file);

      const response = await api.post(`content-types/${id}/sample-file`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response?.data?.url) {
        setUploadedSampleImageUrl(response.data.url);
        toaster.create({
          type: 'success',
          title: 'Sample image uploaded successfully',
        });
      }
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to upload sample file',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const onSubmit = async (data: FormData) => {
    if (!workspaceId || !id) {
      return;
    }

    try {
      await api.put(`content-types/${id}`, {
        name: data.name,
        description: data.description,
        workspace_id: parseInt(workspaceId),
        sport_type_id: data.sport_type_id,
        sample_image_url: uploadedSampleImageUrl || null,
        content_prompt: data.content_prompt,
        img_prompt: data.img_prompt,
      });
      if (workspaceId) {
        loadContentTypes(workspaceId);
      }
      toaster.create({
        type: 'success',
        title: 'Content library updated successfully',
      });
      navigate(`/${workspaceId}/content-types`);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to update content library',
      });
    }
  };

  useEffect(() => {
    async function fetchField() {
      const response = await api.get(`content-types/${id}`);
      if (response.data.data) {
        const contentType = response.data.data;
        if (workspaceId) {
          setIsBaseContentType(
            parseInt(contentType.workspace_id, 10) !== parseInt(workspaceId, 10),
          );
        }

        setUploadedSampleImageUrl(contentType.sample_image_url || null);

        reset({
          name: contentType.name,
          description: contentType.description,
          sport_type_id: contentType.sport_type_id,
          content_prompt: contentType.content_prompt,
          img_prompt: contentType.img_prompt,
        });
      }
    }
    fetchField();
  }, [id, workspaceId, reset]);

  return (
    <WorkspaceLayout>
      <Card.Root minH={'100vh'} mx={'auto'} width={{ md: '90%' }} maxW={'6xl'}>
        <Card.Header>
          <Container maxW={'3xl'}>
            <Heading size='3xl'>Edit custom library</Heading>
          </Container>
        </Card.Header>
        <Card.Body>
          <Container maxW={'3xl'}>
            <form id='edit-content-library' onSubmit={handleSubmit(onSubmit)}>
              <VStack align={'stretch'} gap={4}>
                <Field
                  label='Name'
                  invalid={!!errors.name}
                  errorText={errors.name?.message}
                  disabled={isBaseContentType}
                >
                  <Input {...register('name')} />
                </Field>
                <Field label='Description' disabled={isBaseContentType}>
                  <Textarea {...register('description')} rows={4} />
                </Field>
                <Field label='Content Prompt' disabled={isBaseContentType}>
                  <Textarea {...register('content_prompt')} rows={4} placeholder='Enter the content prompt...' />
                </Field>
                <Field label='Image Prompt' disabled={isBaseContentType}>
                  <Textarea {...register('img_prompt')} rows={4} placeholder='Enter the image prompt...' />
                </Field>

                <Field label='Sample Image' disabled={isBaseContentType}>
                  <VStack align='stretch' gap={3}>
                    <input
                      type='file'
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                      accept='image/*'
                    />
                    <Button
                      variant='outline'
                      onClick={() => fileInputRef.current?.click()}
                      loading={isUploading}
                      disabled={isBaseContentType || isUploading}
                      width='full'
                    >
                      <Icon as={ArrowUpTrayIcon} w={5} h={5} />
                      {uploadedSampleImageUrl ? 'Change Sample Image' : 'Upload Sample Image'}
                    </Button>
                    {uploadedSampleImageUrl && (
                      <Box p={3} bg='gray.50' borderRadius='md'>
                        <Text fontSize='sm' color='gray.600' mb={2}>
                          Current image:
                        </Text>
                        <Text fontSize='sm' fontWeight='medium' wordBreak='break-all' mb={2}>
                          {uploadedSampleImageUrl}
                        </Text>
                        <Image
                          src={uploadedSampleImageUrl}
                          alt='Sample preview'
                          maxWidth='200px'
                          maxHeight='200px'
                          borderRadius='md'
                          border='1px solid'
                          borderColor='gray.200'
                        />
                      </Box>
                    )}
                  </VStack>
                </Field>

                {workspaceId && id && (
                  <ContentTypeActions workspaceId={workspaceId} contentTypeId={id} />
                )}
                {!isBaseContentType && (
                  <Button
                    colorPalette='brand'
                    form='edit-content-library'
                    type='submit'
                    width='full'
                    mt={5}
                    loading={isSubmitting}
                    loadingText='Loading...'
                  >
                    Save
                  </Button>
                )}
              </VStack>
            </form>
          </Container>
        </Card.Body>
      </Card.Root>
    </WorkspaceLayout>
  );
}
